/**
 * SoleadLab 2.0 - Monitoring API Client
 *
 * Typed API client wrapping all Phase 2 monitoring endpoints.
 * Handles JWT auth injection, error normalization, timeouts,
 * and Zod response validation.
 */

import { z } from 'zod';
import type {
  ProjectMonitoringConfig,
  DataPointConfig,
  AlarmRule,
  AlarmRuleGroup,
  AdvancedAlarmRule,
  RuleExclusion,
  CredentialStatus,
  ParsedSchema,
  ActiveAlarmEvent,
  ProviderInfo,
  ProviderHealth,
  DataPointRuntimeState,
  WSSnapshotMessage,
} from '@/types/monitoring';
import {
  ProjectMonitoringConfigSchema,
} from '@/types/monitoring';
import { API_BASE, ApiError, apiFetch, getAuthHeaders } from './apiFetch';

export { ApiError };

// ============================================
// Monitoring Config Endpoints
// ============================================

/** Canonical D1 monitoring config plus its CAS version token (B2). */
export interface LoadedMonitoringConfig {
  config: ProjectMonitoringConfig;
  /** monitoring_configs.version — the optimistic-concurrency token for safe writes. */
  version: number | null;
}

export async function getMonitoringConfig(siteId: string): Promise<LoadedMonitoringConfig> {
  const data = await apiFetch<Record<string, unknown>>(
    `/api/v2/sites/${siteId}/monitoring/config`
  );

  // API returns { site_id, version, config: {...}, globals: {...} } — merge globals into config
  const configObj = (data && typeof data === 'object' && 'config' in data && data.config)
    ? data.config as Record<string, unknown>
    : data;

  // Merge top-level globals back into the config object
  if (data && typeof data === 'object' && 'globals' in data && data.globals) {
    (configObj as Record<string, unknown>).globals = data.globals;
  }

  // Surface the canonical version (CAS token) so the store can write safely (B2).
  const version = (data && typeof data === 'object' && 'version' in data && typeof data.version === 'number')
    ? data.version as number
    : null;

  // Validate with Zod
  const result = ProjectMonitoringConfigSchema.safeParse(configObj);
  const config = result.success ? result.data : (configObj as ProjectMonitoringConfig);
  if (!result.success) console.warn('Monitoring config validation warning:', result.error.issues);

  return { config, version };
}

/** Result of a safe (merge + CAS) monitoring-config save. */
export interface SaveMonitoringConfigResult {
  /** New canonical version after the write (null if the server omitted it). */
  version: number | null;
}

/**
 * SAFE interim save (B2): non-destructive `mode:'merge'` + optimistic-concurrency
 * `expectedVersion`. Never the legacy no-mode wholesale PUT — a stale/blob-derived config
 * can no longer clobber D1-only definitions, and a stale write is rejected with 409
 * (surfaced as an ApiError with status 409). B3 replaces this with granular endpoints.
 */
export async function saveMonitoringConfig(
  siteId: string,
  config: ProjectMonitoringConfig,
  expectedVersion: number
): Promise<SaveMonitoringConfigResult> {
  const res = await apiFetch<{ ok?: boolean; version?: number }>(
    `/api/v2/sites/${siteId}/monitoring/config`,
    { method: 'PUT', body: JSON.stringify({ config, globals: config.globals, mode: 'merge', expectedVersion }) }
  );
  return { version: typeof res?.version === 'number' ? res.version : null };
}

// ============================================
// Canonical granular config write API (B3a — B1a/B1b endpoints)
// ============================================
// Each function applies ONE explicit, version-gated (CAS) mutation server-side and returns
// the full post-write config + new version. `expectedVersion` is the caller's
// monitoringStore.configVersion; a stale write rejects with 409 (ApiError.status === 409).
// These NEVER send `mode:'merge'` — that is the B2 whole-config Publish escape hatch
// (saveMonitoringConfig) ONLY. The config-mutation endpoints return the `config` COLUMN
// (advanced_rules/datapoints/provider_state) WITHOUT globals (a separate column), so the
// store preserves its current globals when applying the result. The globals endpoint
// returns the merged globals instead.

/** Result of a granular CONFIG mutation: full post-write config (config column — NO globals) + new version. */
export interface CanonicalConfigWriteResult {
  ok: boolean;
  changed: boolean;
  version: number | null;
  config: ProjectMonitoringConfig;
}

/** Result of the granular GLOBALS mutation: merged globals (its own column) + new version. */
export interface CanonicalGlobalsWriteResult {
  ok: boolean;
  changed: boolean;
  version: number | null;
  globals: NonNullable<ProjectMonitoringConfig['globals']>;
}

const cfgBase = (siteId: string) => `/api/v2/sites/${siteId}/monitoring/config`;

/**
 * Shared transport for the config-column granular mutations. `expectedVersion` is sent in
 * the body AND mirrored to an `If-Match` header (belt-and-suspenders for DELETE, whose body
 * some intermediaries strip; the server lets the body value win when both are present).
 */
async function writeCanonicalConfig(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  body: Record<string, unknown>,
  expectedVersion: number,
): Promise<CanonicalConfigWriteResult> {
  const res = await apiFetch<Partial<CanonicalConfigWriteResult>>(path, {
    method,
    body: JSON.stringify(body),
    headers: { 'If-Match': String(expectedVersion) },
  });
  return {
    ok: res?.ok ?? false,
    changed: res?.changed ?? false,
    version: typeof res?.version === 'number' ? res.version : null,
    config: (res?.config ?? { datapoints: [], advanced_rules: [] }) as ProjectMonitoringConfig,
  };
}

// ── Advanced rules ────────────────────────────────────────────────────────────
export function addAdvancedRuleApi(siteId: string, rule: AdvancedAlarmRule, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/advanced-rules`, 'POST', { rule, expectedVersion }, expectedVersion);
}
export function updateAdvancedRuleApi(siteId: string, ruleId: string, rule: AdvancedAlarmRule, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/advanced-rules/${ruleId}`, 'PATCH', { rule, expectedVersion }, expectedVersion);
}
export function setAdvancedRuleEnabledApi(siteId: string, ruleId: string, enabled: boolean, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/advanced-rules/${ruleId}`, 'PATCH', { enabled, expectedVersion }, expectedVersion);
}
export function deleteAdvancedRuleApi(siteId: string, ruleId: string, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/advanced-rules/${ruleId}`, 'DELETE', { expectedVersion }, expectedVersion);
}

// ── Datapoints ────────────────────────────────────────────────────────────────
/** Add a NEW datapoint (may carry initial rules/rule_groups — allowed on create). */
export function addDatapointApi(siteId: string, datapoint: DataPointConfig, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints`, 'POST', { datapoint, expectedVersion }, expectedVersion);
}
/**
 * Upsert an EXISTING datapoint's metadata/top-level fields only. Nested `rules` /
 * `rule_groups` MUST be omitted — the endpoint fail-closes (400) if they are present, and
 * preserves the existing nested arrays untouched. (Not wired to a store action in B3a; the
 * map/metadata write path lands in B3b.)
 */
export function upsertDatapointMetaApi(siteId: string, datapoint: Omit<DataPointConfig, 'rules' | 'rule_groups'>, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints`, 'POST', { datapoint, expectedVersion }, expectedVersion);
}
/** Patch narrow datapoint metadata (entity_id / display_name / enabled only). */
export function patchDatapointApi(
  siteId: string,
  datapointId: string,
  patch: { entity_id?: string; display_name?: string; enabled?: boolean },
  expectedVersion: number,
) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}`, 'PATCH', { ...patch, expectedVersion }, expectedVersion);
}
export function deleteDatapointApi(siteId: string, datapointId: string, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}`, 'DELETE', { expectedVersion }, expectedVersion);
}

// ── Datapoint rules ─────────────────────────────────────────────────────────────
export function addDatapointRuleApi(siteId: string, datapointId: string, rule: AlarmRule, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rules`, 'POST', { rule, expectedVersion }, expectedVersion);
}
export function updateDatapointRuleApi(siteId: string, datapointId: string, ruleId: string, rule: AlarmRule, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rules/${ruleId}`, 'PATCH', { rule, expectedVersion }, expectedVersion);
}
export function setDatapointRuleEnabledApi(siteId: string, datapointId: string, ruleId: string, enabled: boolean, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rules/${ruleId}`, 'PATCH', { enabled, expectedVersion }, expectedVersion);
}
export function setDatapointRuleExclusionsApi(siteId: string, datapointId: string, ruleId: string, exclusions: RuleExclusion[], expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rules/${ruleId}`, 'PATCH', { exclusions, expectedVersion }, expectedVersion);
}
export function deleteDatapointRuleApi(siteId: string, datapointId: string, ruleId: string, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rules/${ruleId}`, 'DELETE', { expectedVersion }, expectedVersion);
}

// ── Rule groups ───────────────────────────────────────────────────────────────
export function addRuleGroupApi(siteId: string, datapointId: string, group: AlarmRuleGroup, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rule-groups`, 'POST', { group, expectedVersion }, expectedVersion);
}
export function updateRuleGroupApi(siteId: string, datapointId: string, groupId: string, group: AlarmRuleGroup, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rule-groups/${groupId}`, 'PATCH', { group, expectedVersion }, expectedVersion);
}
export function setRuleGroupEnabledApi(siteId: string, datapointId: string, groupId: string, enabled: boolean, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rule-groups/${groupId}`, 'PATCH', { enabled, expectedVersion }, expectedVersion);
}
export function deleteRuleGroupApi(siteId: string, datapointId: string, groupId: string, expectedVersion: number) {
  return writeCanonicalConfig(`${cfgBase(siteId)}/datapoints/${datapointId}/rule-groups/${groupId}`, 'DELETE', { expectedVersion }, expectedVersion);
}

// ── Globals (separate column — merged-globals + version response) ───────────────
export async function patchGlobalsApi(
  siteId: string,
  globalsPartial: Partial<NonNullable<ProjectMonitoringConfig['globals']>>,
  expectedVersion: number,
): Promise<CanonicalGlobalsWriteResult> {
  const res = await apiFetch<Partial<CanonicalGlobalsWriteResult>>(`${cfgBase(siteId)}/globals`, {
    method: 'PATCH',
    body: JSON.stringify({ globals: globalsPartial, expectedVersion }),
    headers: { 'If-Match': String(expectedVersion) },
  });
  return {
    ok: res?.ok ?? false,
    changed: res?.changed ?? false,
    version: typeof res?.version === 'number' ? res.version : null,
    globals: (res?.globals ?? {}) as NonNullable<ProjectMonitoringConfig['globals']>,
  };
}

// ============================================
// Credential Endpoints
// ============================================

export async function getCredentialStatus(siteId: string): Promise<CredentialStatus> {
  const raw = await apiFetch<CredentialStatus>(
    `/api/v2/sites/${siteId}/monitoring/credentials`
  );
  // Derive convenience top-level fields from the first credential
  if (raw.hasCredentials && raw.credentials?.length) {
    const first = raw.credentials[0];
    raw.provider = first.provider_name;
    raw.createdAt = first.created_at;
  }
  return raw;
}

export async function setCredentials(
  siteId: string,
  credentials: {
    provider_name: string;
    api_base_url: string;
    api_key: string;
    api_secret?: string;
    system_id?: string;
    auth_type?: string;
    extra_config?: Record<string, unknown>;
  }
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/v2/sites/${siteId}/monitoring/credentials`,
    { method: 'POST', body: JSON.stringify(credentials) }
  );
}

export async function deleteCredentials(siteId: string, provider: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/v2/sites/${siteId}/monitoring/credentials?provider=${encodeURIComponent(provider)}`,
    { method: 'DELETE' }
  );
}

export async function testConnection(
  siteId: string,
  providerName?: string
): Promise<{ ok: boolean; message: string; provider: string }> {
  return apiFetch<{ ok: boolean; message: string; provider: string }>(
    `/api/v2/sites/${siteId}/monitoring/test-connection`,
    {
      method: 'POST',
      body: JSON.stringify({ provider_name: providerName }),
    }
  );
}

// ============================================
// Schema Endpoints
// ============================================

export async function getSchema(siteId: string): Promise<ParsedSchema | null> {
  try {
    return await apiFetch<ParsedSchema>(
      `/api/v2/sites/${siteId}/monitoring/schema`
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function uploadSchema(
  siteId: string,
  schema: ParsedSchema
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/v2/sites/${siteId}/monitoring/schema`,
    { method: 'PUT', body: JSON.stringify({ schema }) }
  );
}

// ============================================
// Data Endpoints
// ============================================

export async function getLatestData(siteId: string): Promise<{
  data: Record<string, Record<string, number | string | null>>;
  timestamp: string | null;
  alarms: ActiveAlarmEvent[];
  runtimeStates: DataPointRuntimeState[];
}> {
  return apiFetch(
    `/api/v2/sites/${siteId}/monitoring/data/latest`
  );
}

export async function pushData(
  siteId: string,
  jsonPayload: unknown
): Promise<{
  ok: boolean;
  entities_parsed: number;
  schema: unknown;
  alarms_triggered: unknown[];
  timestamp: string;
}> {
  return apiFetch(
    `/api/v2/sites/${siteId}/monitoring/data/push`,
    { method: 'POST', body: JSON.stringify({ measurement_data: jsonPayload }) }
  );
}

// ============================================
// Alarm Endpoints
// ============================================

/**
 * Cross-site active-alarm rollup for the dashboard map.
 * Returns counts keyed by site_id. Sites with zero active alarms are omitted.
 */
export interface AlarmSummaryResponse {
  counts: Record<string, { critical: number; warning: number }>;
}

export async function listAlarmSummary(): Promise<AlarmSummaryResponse> {
  return apiFetch<AlarmSummaryResponse>(`/api/v2/monitoring/alarms/summary`);
}

export async function listAlarms(
  siteId: string,
  state?: 'active' | 'acknowledged'
): Promise<ActiveAlarmEvent[]> {
  const query = state ? `?state=${state}` : '';
  const result = await apiFetch<{ items: ActiveAlarmEvent[] } | ActiveAlarmEvent[]>(
    `/api/v2/sites/${siteId}/monitoring/alarms${query}`
  );
  // API returns paginated { items: [...] } but callers expect a flat array
  if (Array.isArray(result)) return result;
  return (result as { items: ActiveAlarmEvent[] }).items ?? [];
}

export async function acknowledgeAlarm(
  siteId: string,
  alarmId: string,
  notes?: string
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/v2/sites/${siteId}/monitoring/alarms/${alarmId}/ack`,
    { method: 'PUT', body: JSON.stringify({ notes }) }
  );
}

export async function getAlarmHistory(
  siteId: string,
  options?: { limit?: number; page?: number; severity?: string }
): Promise<{ items: ActiveAlarmEvent[]; total: number; page: number; limit: number; hasMore: boolean }> {
  // The handler paginates by 1-based `page` and responds with `items` (NOT `offset`
  // / `alarms`) — matching those here is what makes the D1 alarm history actually
  // render (start + resolve log for both datapoint and advanced alarms).
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.page) params.set('page', String(options.page));
  if (options?.severity) params.set('severity', options.severity);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(
    `/api/v2/sites/${siteId}/monitoring/alarms/history${query}`
  );
}

// ============================================
// Provider Endpoints
// ============================================

export async function listProviders(): Promise<{ providers: ProviderInfo[] }> {
  return apiFetch<{ providers: ProviderInfo[] }>(
    '/api/v2/monitoring/providers'
  );
}

// ============================================
// Schema Discovery (live from provider)
// ============================================

export async function discoverSchema(siteId: string): Promise<ParsedSchema> {
  return apiFetch<ParsedSchema>(
    `/api/v2/sites/${siteId}/monitoring/schema/discover`,
    { method: 'POST' },
    30_000 // Longer timeout — provider API round-trip
  );
}

// ============================================
// Live Control Endpoints (DO)
// ============================================

export async function startLive(siteId: string): Promise<{ ok: boolean; alreadyRunning?: boolean }> {
  return apiFetch(
    `/api/v2/sites/${siteId}/monitoring/live/start`,
    { method: 'POST' }
  );
}

export async function stopLive(siteId: string): Promise<{ ok: boolean }> {
  return apiFetch(
    `/api/v2/sites/${siteId}/monitoring/live/stop`,
    { method: 'POST' }
  );
}

export async function refreshLive(siteId: string): Promise<{ ok: boolean }> {
  return apiFetch(
    `/api/v2/sites/${siteId}/monitoring/live/refresh`,
    { method: 'POST' }
  );
}

export async function getLiveHealth(siteId: string): Promise<ProviderHealth> {
  return apiFetch<ProviderHealth>(
    `/api/v2/sites/${siteId}/monitoring/live/health`
  );
}

export async function getLiveSnapshot(siteId: string): Promise<WSSnapshotMessage> {
  return apiFetch<WSSnapshotMessage>(
    `/api/v2/sites/${siteId}/monitoring/live/snapshot`
  );
}

// ============================================
// WebSocket Helper
// ============================================

/**
 * Build the WebSocket URL for live monitoring.
 * Auth is passed via query param (WS upgrade doesn't support headers in browsers).
 */
export function buildWSUrl(siteId: string): string {
  const headers = getAuthHeaders();
  const authHeader = headers['Authorization'];
  const token = authHeader ? authHeader.replace('Bearer ', '') : null;
  const base = API_BASE.replace(/^http/, 'ws');
  return `${base}/api/v2/sites/${siteId}/monitoring/live/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
}

// Validate unused import is used
void z;
